import { useState, useEffect, useCallback } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { MoreHorizontal, CheckCircle, XCircle, Pencil, Trash2, Users, MessageSquare, ArrowDownRight, DollarSign, Clock, UserCheck, Mail } from "lucide-react";


export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [adSettings, setAdSettings] = useState({ top: "", inline: "", sidebar_1: "", sidebar_2: "", bottom: "" });
  const [adLoading, setAdLoading] = useState(false);
  const [pointsSettings, setPointsSettings] = useState({ conversion_rate: 0.001, tier_1_points: 3, tier_2_points: 2, tier_3_points: 1 });
  const [pointsLoading, setPointsLoading] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, withdrawalsRes, statsRes, adRes, pointsRes] = await Promise.all([
        api.get(`/admin/users`),
        api.get(`/admin/withdrawals`),
        api.get(`/admin/stats`),
        api.get(`/admin/ad-settings`),
        api.get(`/admin/points-settings`),
      ]);
      setUsers(usersRes.data);
      setWithdrawals(withdrawalsRes.data);
      setStats(statsRes.data);
      setAdSettings(adRes.data || { top: "", inline: "", sidebar_1: "", sidebar_2: "", bottom: "" });
      setPointsSettings(pointsRes.data || { conversion_rate: 0.001, tier_1_points: 3, tier_2_points: 2, tier_3_points: 1 });
    } catch {
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/approve`, {});
      toast.success("User approved");
      fetchData();
    } catch {
      toast.error("Failed to approve user");
    }
  };

  const handleReject = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/reject`, {});
      toast.success("User rejected");
      fetchData();
    } catch {
      toast.error("Failed to reject user");
    }
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status,
      balance: user.balance ?? 0,
      new_password: "",
    });
  };

  const handleSaveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.new_password) delete payload.new_password;
      await api.put(`/admin/users/${editUser.id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteConfirm.id}`);
      toast.success("User deleted");
      setDeleteConfirm(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const statusBadge = (status) => {
    const variants = {
      approved: "bg-emerald-50 text-emerald-700",
      pending: "bg-amber-50 text-amber-700",
      rejected: "bg-red-50 text-red-700",
      suspended: "bg-gray-100 text-gray-700",
    };
    return (
      <Badge variant="secondary" className={`border-0 ${variants[status] || ""}`}>
        {status}
      </Badge>
    );
  };

  const pendingCount = users.filter((u) => u.status === "pending").length;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 md:py-12 animate-fade-in" data-testid="admin-page">
      <div className="mb-8">
        <p className="text-xs font-medium tracking-widest uppercase text-emerald-600 mb-2 animate-fade-in-up stagger-1">Management</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight animate-fade-in-up stagger-2" data-testid="admin-title">
          Admin Panel
        </h1>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="admin-stats-grid">
          <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-sm bg-muted flex items-center justify-center">
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">Total Users</p>
                  <p className="font-heading text-xl font-bold" data-testid="stat-total-users">{stats.total_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-sm bg-amber-50 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">Pending</p>
                  <p className="font-heading text-xl font-bold" data-testid="stat-pending-users">{stats.pending_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-sm bg-emerald-50 flex items-center justify-center">
                  <UserCheck className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">Approved</p>
                  <p className="font-heading text-xl font-bold" data-testid="stat-approved-users">{stats.approved_users}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-sm bg-emerald-50 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">Total Balance</p>
                  <p className="font-heading text-xl font-bold" data-testid="stat-total-balance">${stats.total_balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border rounded-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-sm bg-muted flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">Withdrawals</p>
                  <p className="font-heading text-xl font-bold" data-testid="stat-withdrawal-count">{stats.withdrawal_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="users" data-testid="admin-tab-users">
            <Users className="h-4 w-4 mr-2" /> Users{pendingCount > 0 ? ` (${pendingCount} pending)` : ""}
          </TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="admin-tab-withdrawals">
            <ArrowDownRight className="h-4 w-4 mr-2" /> Withdrawals
          </TabsTrigger>
          <TabsTrigger value="ads" data-testid="admin-tab-ads">
            <DollarSign className="h-4 w-4 mr-2" /> Ad Settings
          </TabsTrigger>
          <TabsTrigger value="points" data-testid="admin-tab-points">
            <MessageSquare className="h-4 w-4 mr-2" /> Points
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="admin-users-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">All Users</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Email Verified</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Points</TableHead>
                    <TableHead>Social</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role}</Badge>
                      </TableCell>
                      <TableCell>{statusBadge(u.status)}</TableCell>
                      <TableCell>
                        {u.email_verified ? (
                          <Mail className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <Mail className="h-4 w-4 text-muted-foreground/40" />
                        )}
                      </TableCell>
                      <TableCell>${(u.balance ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="font-medium">{(u.points ?? 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm">
                        {u.social_media_url ? (
                          <a href={u.social_media_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline truncate max-w-[100px] inline-block" data-testid={`user-social-${u.id}`}>
                            Link
                          </a>
                        ) : (
                          <span className="text-muted-foreground/40">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`admin-user-actions-${u.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {u.status === "pending" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleApprove(u.id)}
                                  data-testid={`approve-user-${u.id}`}
                                >
                                  <CheckCircle className="mr-2 h-4 w-4 text-emerald-600" /> Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleReject(u.id)}
                                  data-testid={`reject-user-${u.id}`}
                                >
                                  <XCircle className="mr-2 h-4 w-4 text-red-600" /> Reject
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}>
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteConfirm(u)}
                              className="text-red-600"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Withdrawals Tab */}
        <TabsContent value="withdrawals">
          <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="admin-withdrawals-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">All Withdrawals</CardTitle>
            </CardHeader>
            <CardContent>
              {withdrawals.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No withdrawals yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {withdrawals.map((w, i) => (
                      <TableRow key={w.id || i} data-testid={`admin-withdrawal-row-${i}`}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {new Date(w.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{w.user_email}</TableCell>
                        <TableCell className="font-medium">${w.amount.toFixed(2)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {(w.payment_method || "-").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[120px] truncate">
                          {w.payment_address || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={`border-0 ${
                            w.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                            w.status === "pending" ? "bg-amber-50 text-amber-700" :
                            w.status === "rejected" ? "bg-red-50 text-red-700" :
                            "bg-gray-50 text-gray-700"
                          }`}>
                            {w.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {w.status === "pending" && (
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={async () => {
                                  try {
                                    await api.put(`/admin/withdrawals/${w.id}/approve`, {});
                                    toast.success("Withdrawal approved");
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Failed to approve");
                                  }
                                }}
                                data-testid={`approve-withdrawal-${w.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={async () => {
                                  try {
                                    await api.put(`/admin/withdrawals/${w.id}/reject`, {});
                                    toast.success("Withdrawal rejected & refunded");
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Failed to reject");
                                  }
                                }}
                                data-testid={`reject-withdrawal-${w.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ads Tab */}
        <TabsContent value="ads">
          <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="admin-ads-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">Global Ad Settings</CardTitle>
              <p className="text-sm text-muted-foreground">These ad codes appear on all user landing pages. Paste HTML/script ad code for each slot.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Top Leaderboard (728x90)</Label>
                <Input
                  placeholder="Ad code for top banner"
                  value={adSettings.top}
                  onChange={(e) => setAdSettings((s) => ({ ...s, top: e.target.value }))}
                  data-testid="global-ad-slot-top"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Inline Rectangle (336x280)</Label>
                <Input
                  placeholder="Ad code for inline ad"
                  value={adSettings.inline}
                  onChange={(e) => setAdSettings((s) => ({ ...s, inline: e.target.value }))}
                  data-testid="global-ad-slot-inline"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Sidebar 1 (300x250)</Label>
                  <Input
                    placeholder="Sidebar ad 1"
                    value={adSettings.sidebar_1}
                    onChange={(e) => setAdSettings((s) => ({ ...s, sidebar_1: e.target.value }))}
                    data-testid="global-ad-slot-sidebar1"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Sidebar 2 (300x600)</Label>
                  <Input
                    placeholder="Sidebar ad 2"
                    value={adSettings.sidebar_2}
                    onChange={(e) => setAdSettings((s) => ({ ...s, sidebar_2: e.target.value }))}
                    data-testid="global-ad-slot-sidebar2"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Bottom Leaderboard (728x90)</Label>
                <Input
                  placeholder="Ad code for bottom banner"
                  value={adSettings.bottom}
                  onChange={(e) => setAdSettings((s) => ({ ...s, bottom: e.target.value }))}
                  data-testid="global-ad-slot-bottom"
                />
              </div>
              <Button
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={adLoading}
                onClick={async () => {
                  setAdLoading(true);
                  try {
                    await api.put(`/admin/ad-settings`, adSettings);
                    toast.success("Ad settings saved — applies to all landing pages");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to save");
                  } finally {
                    setAdLoading(false);
                  }
                }}
                data-testid="global-ad-save-btn"
              >
                {adLoading ? "Saving..." : "Save Ad Settings"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Points Tab */}
        <TabsContent value="points">
          <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="admin-points-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">Points & Conversion Settings</CardTitle>
              <p className="text-sm text-muted-foreground">Users earn points from unique daily visitors on their landing pages. Points are converted to balance at the rate below.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tier 1 Points</Label>
                  <p className="text-xs text-muted-foreground">US, UK, CA, AU, DE, etc.</p>
                  <Input
                    type="number"
                    value={pointsSettings.tier_1_points}
                    onChange={(e) => setPointsSettings((s) => ({ ...s, tier_1_points: parseInt(e.target.value) || 0 }))}
                    data-testid="points-tier1"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tier 2 Points</Label>
                  <p className="text-xs text-muted-foreground">BR, MX, IN, IT, ES, etc.</p>
                  <Input
                    type="number"
                    value={pointsSettings.tier_2_points}
                    onChange={(e) => setPointsSettings((s) => ({ ...s, tier_2_points: parseInt(e.target.value) || 0 }))}
                    data-testid="points-tier2"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tier 3 Points</Label>
                  <p className="text-xs text-muted-foreground">All other countries</p>
                  <Input
                    type="number"
                    value={pointsSettings.tier_3_points}
                    onChange={(e) => setPointsSettings((s) => ({ ...s, tier_3_points: parseInt(e.target.value) || 0 }))}
                    data-testid="points-tier3"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Conversion Rate ($ per point)</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={pointsSettings.conversion_rate}
                  onChange={(e) => setPointsSettings((s) => ({ ...s, conversion_rate: parseFloat(e.target.value) || 0 }))}
                  data-testid="points-conversion-rate"
                />
                <p className="text-xs text-muted-foreground">Example: 0.001 means 1000 points = $1.00</p>
              </div>
              <div className="flex gap-3">
                <Button
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={pointsLoading}
                  onClick={async () => {
                    setPointsLoading(true);
                    try {
                      await api.put(`/admin/points-settings`, pointsSettings);
                      toast.success("Points settings saved");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to save");
                    } finally {
                      setPointsLoading(false);
                    }
                  }}
                  data-testid="points-save-btn"
                >
                  {pointsLoading ? "Saving..." : "Save Settings"}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-sm"
                  disabled={convertLoading}
                  onClick={async () => {
                    setConvertLoading(true);
                    try {
                      const { data } = await api.post(`/admin/convert-points`);
                      toast.success(`Converted ${data.total_points_converted.toLocaleString()} points to $${data.total_balance_added.toFixed(2)} for ${data.message.match(/\d+/)?.[0] || 0} users`);
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Conversion failed");
                    } finally {
                      setConvertLoading(false);
                    }
                  }}
                  data-testid="points-convert-btn"
                >
                  {convertLoading ? "Converting..." : "Convert All Points to Balance"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
            <DialogDescription>Update user details, password, and ad settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editForm.name || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={editForm.email || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  data-testid="edit-user-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="edit-user-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Balance ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.balance ?? 0}
                  onChange={(e) => setEditForm((f) => ({ ...f, balance: parseFloat(e.target.value) || 0 }))}
                  data-testid="edit-user-balance"
                />
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input
                  type="password"
                  placeholder="Leave blank to keep current"
                  value={editForm.new_password || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, new_password: e.target.value }))}
                  data-testid="edit-user-password"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edit-user-save-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="rounded-sm" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deleteConfirm?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm" data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
